import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { ConfigBlock } from 'components/molecules/settings/styled';
import { InputDescription, InputTitle } from 'components/atoms/styleds';
import RadioButtons from 'components/atoms/RadioButtons';
import NumberInput from 'components/atoms/NumberInput';
import Input from 'components/atoms/Input';
import InfoTooltip from 'components/atoms/InfoTooltip';
import Select from 'components/atoms/Select';
import { Divider } from 'components/atoms/styleds';
import { getErrorByKey } from 'utils';
import { ReactComponent as DeleteIcon } from 'assets/images/delete.svg';
import BaseErrors from 'components/atoms/BaseError';

import { CreateLink } from 'components/molecules/user/Menu/styled';

import { useServiceForm } from 'hooks/formStates/settings';
import WeekTimes from './WeekTimes';
import RoomChangeItem from './RoomChangeItem';
import { Wrapper } from './styled';
import { nanoid } from 'nanoid';

const averageDurations = [
  {
    id: '30_minutes',
    title: '30 minuti',
  },
  {
    id: '45_minutes',
    title: '45 minuti',
  },
  {
    id: '60_minutes',
    title: '1 h',
  },
  {
    id: '90_minutes',
    title: '1.5 h',
  },
  {
    id: '120_minutes',
    title: '2 h',
  },
  {
    id: '150_minutes',
    title: '2.5 h',
  },
  {
    id: '180_minutes',
    title: '3 h',
  },
];

const ServiceForm = ({
  localState,
  onSelectTime,
  onChange,
  onClickChip,
  errors,
  isEditMode,
  onChangeRoom,
  rooms,
}) => {
  const [localVal, setLocalVal] = useState(
    localState.auto_confirmation === undefined
      ? false
      : localState.auto_confirmation,
  );

  useEffect(() => {
    setLocalVal(
      localState.auto_confirmation === undefined
        ? false
        : localState.auto_confirmation,
    );
  }, [localState.auto_confirmation]);

  useEffect(() => {
    // Ensure there is always at least one room
    if (
      !Array.isArray(localState.services_rooms_attributes) ||
      localState.services_rooms_attributes.length === 0
    ) {
      onChange({
        target: {
          name: 'services_rooms_attributes',
          value: [
            {
              room_id: '',
              total_seats: '',
              uuid: nanoid(),
            },
          ],
        },
      });
    }
  }, [localState.services_rooms_attributes, onChange]);

  const showConfirmationStatuses = localState.auto_confirmation_type !== 'none';

  const autoAcceptRadioItems = [
    {
      value: true,
      label: 'Sì',
      relationView: (
        <>
          <InputDescription style={{ marginTop: '16px', marginBottom: '8px' }}>
            Accetto <strong>automaticamente</strong> le prenotazioni online{' '}
            <InfoTooltip />
          </InputDescription>
          <Select
            name='auto_confirmation_type'
            label='Scegli tra le opzioni proposte'
            defaultLabel='Scegli tra le opzioni proposte'
            selectedKey={localState.auto_confirmation_type}
            items={[
              {
                id: 'always',
                title: 'Sempre',
              },
              {
                id: 'before_start',
                title: "Fino all'inizio del servizio",
              },
              {
                id: '30_minutes',
                title: "Fino a 30 min prima dell'inizio del servizio",
              },
              {
                id: '60_minutes',
                title: "Fino a 60 min prima dell'inizio del servizio",
              },
            ]}
            onChange={onChange}
            error={getErrorByKey(errors, 'auto_confirmation_type') || null}
          />
        </>
      ),
    },
    {
      value: false,
      label: 'No, desidero poterle rifiutare',
    },
  ];

  const timeSlotsRadioItems = [
    {
      value: '30_minutes',
      label: '30 minuti',
    },
    {
      value: '15_minutes',
      label: '15 minuti',
    },
  ];

  const onChangeLocalConfirmationType = useCallback(
    (e) => {
      const {
        target: { value },
      } = e;
      const boolValue = value === 'true' || value === true;
      setLocalVal(boolValue);
      onChange({
        target: { name: 'auto_confirmation', value: boolValue },
      });
    },
    [onChange],
  );

  const selectedRooms = useMemo(() => {
    const servicesRooms = localState?.services_rooms_attributes;
    console.log(`[index.js::172]:servicesRooms`, servicesRooms);
    if (!Array.isArray(servicesRooms)) {
      return [];
    }
    return servicesRooms.filter((i) => (!!i.roomId || !!i.room) && !i._destroy);
  }, [localState?.services_rooms_attributes]);

  const filteredRooms = useMemo(() => {
    return rooms.filter((room) => {
      return !selectedRooms?.find((sRoom) => {
        console.log(`[index.js::182]:sRoom.room`, room.id, sRoom);
        return sRoom.room && !sRoom.roomId
          ? +sRoom.roomId === room.id
          : +sRoom.roomId === room.id;
      });
    });
  }, [rooms, selectedRooms]);

  const allowAddNewRoom = useMemo(() => {
    return filteredRooms.length > 0;
  }, [filteredRooms]);

  const getRoomsList = (item) => {
    const currentRoomId = item.roomId;
    const availableRooms = rooms.filter((room) => {
      // Include current room if it's selected
      if (currentRoomId && room.id === parseInt(currentRoomId)) {
        return true;
      }
      // Include rooms that are not selected by other items
      return !selectedRooms.find((sRoom) => {
        return (
          sRoom.uuid !== item.uuid &&
          ((sRoom.room && sRoom.room.id === room.id) ||
            (sRoom.roomId && parseInt(sRoom.roomId) === room.id))
        );
      });
    });
    return availableRooms.map((room) => ({
      id: room.id.toString(),
      title: room.title,
    }));
  };

  const handleRoomSelection = (e, index) => {
    const { value } = e.target;
    const selectedRoom = rooms.find((room) => room.id === parseInt(value));

    if (selectedRoom) {
      const currentRooms = Array.isArray(localState.services_rooms_attributes)
        ? [...localState.services_rooms_attributes]
        : [];

      currentRooms[index] = {
        ...currentRooms[index],
        roomId: selectedRoom.id,
      };

      onChange({
        target: {
          name: 'services_rooms_attributes',
          value: currentRooms,
        },
      });
    }
  };

  const handleSeatsChange = (e, index) => {
    const { value } = e.target;
    const currentRooms = Array.isArray(localState.services_rooms_attributes)
      ? [...localState.services_rooms_attributes]
      : [];

    currentRooms[index] = {
      ...currentRooms[index],
      total_seats: value,
    };

    onChange({
      target: {
        name: 'services_rooms_attributes',
        value: currentRooms,
      },
    });
  };

  const handleDeleteRoom = (index) => {
    if (localState.services_rooms_attributes.length > 1) {
      const currentRooms = [...localState.services_rooms_attributes].map(
        (room, i) => {
          if (i === index) {
            return {
              ...room,
              _destroy: true,
            };
          }
          return room;
        },
      );

      onChange({
        target: {
          name: 'services_rooms_attributes',
          value: currentRooms,
        },
      });
    }
  };

  const handleAddRoom = () => {
    const currentRooms = Array.isArray(localState.services_rooms_attributes)
      ? [...localState.services_rooms_attributes]
      : [];

    currentRooms.push({
      room_id: '',
      total_seats: '',
      uuid: nanoid(),
    });

    onChange({
      target: {
        name: 'services_rooms_attributes',
        value: currentRooms,
      },
    });
  };

  const handleDebugClick = () => {
    console.log('Form Data:', {
      // Basic settings
      id: localState.id,
      title: localState.title,
      service_duration: localState.service_duration,
      total_seats: localState.total_seats,
      slot_duration: localState.slot_duration,
      slot_seats: localState.slot_seats,
      booking_max_person_count: localState.booking_max_person_count,

      // Confirmation settings
      auto_confirmation: localState.auto_confirmation,
      auto_confirmation_type: localState.auto_confirmation_type,
      prereservation_time: localState.prereservation_time,

      // Room settings
      services_rooms_attributes: localState.services_rooms_attributes?.map(
        (room) => ({
          ...room,
          room_details: rooms.find((r) => r.id === parseInt(room.room_id)),
        }),
      ),

      // Time settings
      service_opening_hours_attributes:
        localState.service_opening_hours_attributes,
    });
  };

  console.log(
    `[index.js::320]:localState.services_rooms_attributes`,
    localState.services_rooms_attributes,
  );

  return (
    <Wrapper>
      <ConfigBlock showBorder={true} desktop_pb={60}>
        <InputTitle>1. Nome del servizio</InputTitle>
        <InputDescription>
          Scrivi il nome che identifica il servizio (esempi: pranzo o cena).
        </InputDescription>
        <Input
          name='title'
          label='Nome del servizio'
          placeholder='Es: Pranzo 1° turno'
          value={localState.title}
          onChange={onChange}
          error={getErrorByKey(errors, 'title') || null}
        />
        <InputDescription>
          Indica il numero totale di coperti prenotabili per questo servizio.
        </InputDescription>
        <Input
          type='number'
          name='total_seats'
          label='Totale coperti prenotabili'
          placeholder='Es: 30'
          value={localState.total_seats}
          onChange={onChange}
          error={getErrorByKey(errors, 'total_seats') || null}
        />
        <InputDescription>
          Indica la durata media della permanenza dei clienti.
        </InputDescription>
        <Select
          onChange={onChange}
          name='service_duration'
          selectedKey={localState.service_duration}
          label='Durata permanenza media'
          items={averageDurations}
          error={getErrorByKey(errors, 'service_duration') || null}
        />
        {/* <InputDescription>
          Indica il costo di prenotazione a persona per questo servizio (facoltativo).
        </InputDescription>
        <Input
          type='text'
          name='price'
          label='Costo di prenotazione a persona'
          placeholder='Es: 3,00€'
          value={localState.price}
          onChange={onChange}
          error={getErrorByKey(errors, 'price')}
        /> */}
        <InputDescription>
          Indica il numero massimo di persone per prenotazione/tavolo per questo
          servizio.
        </InputDescription>
        <Input
          type='number'
          name='booking_max_person_count'
          label='Numero massimo di persone per tavolo'
          placeholder='Es: 6'
          value={localState.booking_max_person_count}
          onChange={onChange}
          error={getErrorByKey(errors, 'booking_max_person_count') || null}
        />
        <InputDescription>
          Seleziona i giorni e gli orari in cui è disponibile il servizio
          durante gli orari di apertura che hai già indicato.
        </InputDescription>

        <WeekTimes
          data={localState}
          onSelectTime={onSelectTime}
          onClickChip={onClickChip}
          errors={errors}
          isEditMode={isEditMode}
        />
      </ConfigBlock>
      <ConfigBlock showBorder={true} desktop_pb={60}>
        <InputTitle>2. Scelta della sala</InputTitle>
        <InputDescription>
          Associa il servizio a una o più sale/dehors.
        </InputDescription>

        {/* Display room validation errors */}
        {errors?.services_rooms_attributes &&
          typeof errors.services_rooms_attributes === 'string' && (
            <div
              style={{
                backgroundColor: '#ffebee',
                color: '#c62828',
                padding: '8px 12px',
                borderRadius: '4px',
                marginBottom: '16px',
                border: '1px solid #ffcdd2',
                fontSize: '14px',
              }}
            >
              {errors.services_rooms_attributes}
            </div>
          )}

        {Array.isArray(localState.services_rooms_attributes) &&
          localState.services_rooms_attributes
            .filter((item) => !item._destroy)
            .map((item, index) => {
              console.log(
                `[index.js::426]:getRoomsList(item)`,
                getRoomsList(item),
                item.roomId?.toString(),
              );
              return (
                <Fragment key={item.uuid || index}>
                  <div style={{ marginBottom: '24px' }}>
                    <Select
                      name={`room_${index}`}
                      label='Seleziona la sala/dehors'
                      defaultLabel='Seleziona la sala/dehors'
                      selectedKey={item.roomId?.toString()}
                      items={getRoomsList(item)}
                      onChange={(e) => handleRoomSelection(e, index)}
                      error={
                        getErrorByKey(
                          errors,
                          `services_rooms_attributes.${index}.room_id`,
                        ) || null
                      }
                    />
                    <InputDescription>
                      Indica il numero di coperti disponibili per questa
                      sala/dehors.
                    </InputDescription>
                    <Input
                      type='number'
                      name={`seats_${index}`}
                      label='Totale coperti'
                      placeholder='Es: 30'
                      value={item.total_seats}
                      onChange={(e) => handleSeatsChange(e, index)}
                      error={
                        getErrorByKey(
                          errors,
                          `services_rooms_attributes.${index}.total_seats`,
                        ) || null
                      }
                    />
                    {localState.services_rooms_attributes.length > 1 && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          marginTop: '8px',
                          color: '#666',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleDeleteRoom(index)}
                      >
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: '700',
                            color: '#636262',
                            marginRight: '12px',
                          }}
                        >
                          Rimuovi sala/dehors
                        </span>
                        <DeleteIcon />
                      </div>
                    )}
                    <Divider
                      style={{
                        width: '100%',
                        marginTop: '20px',
                        marginBottom: '20px',
                      }}
                    />
                  </div>
                </Fragment>
              );
            })}

        {allowAddNewRoom && (
          <CreateLink
            role='presentation'
            as='span'
            onClick={handleAddRoom}
            style={{ color: '#ff6b00' }}
          >
            + Aggiungi sala/dehors
          </CreateLink>
        )}
      </ConfigBlock>
      <ConfigBlock showBorder={false}>
        <InputTitle>3. Gestione prenotazioni</InputTitle>
        <InputDescription>
          Permetti la prenotazione online fino a:
        </InputDescription>
        <Select
          name='prereservation_time'
          label='Scegli tra le opzioni proposte'
          defaultLabel='opzioni proposte'
          selectedKey={localState.prereservation_time}
          items={[
            {
              id: 'none',
              title: "All'arrivo degli ospiti",
            },
            {
              id: '15_minutes',
              title: "15 min prima dell'arrivo degli ospiti",
            },
            {
              id: '30_minutes',
              title: "30 min prima dell'arrivo degli ospiti",
            },
            {
              id: '60_minutes',
              title: "60 min prima dell'arrivo degli ospiti",
            },
            {
              id: '120_minutes',
              title: "2 ore prima dell'arrivo degli ospiti",
            },
            {
              id: '180_minutes',
              title: "3 ore prima dell'arrivo degli ospiti",
            },
          ]}
          onChange={onChange}
          error={getErrorByKey(errors, 'prereservation_time') || null}
        />
        <Divider mb={20} mobileMb={20} />
        <InputTitle>
          4. Accetti automaticamente le prenotazioni online ?
        </InputTitle>
        <RadioButtons
          items={autoAcceptRadioItems}
          onChange={onChangeLocalConfirmationType}
          selected={localVal}
          name='auto_confirmation'
        />
        <BaseErrors errors={getErrorByKey(errors, 'auto_confirmation')} />
        <Divider mb={20} mobileMb={20} />
        <InputTitle>
          5. Gestisci le prenotazioni con le fasce orarie da:
        </InputTitle>
        <RadioButtons
          items={timeSlotsRadioItems}
          name='slot_duration'
          onChange={onChange}
          selected={localState.slot_duration || '30_minutes'}
        />
        <BaseErrors errors={getErrorByKey(errors, 'slot_duration')} />
        <InputTitle mb={20}>
          6. Indica la disponibilità massima di coperti per fascia oraria.
        </InputTitle>
        <Input
          type='number'
          name='slot_seats'
          label='Coperti per fascia oraria'
          placeholder='Es: 6'
          value={localState.slot_seats}
          onChange={onChange}
          error={getErrorByKey(errors, 'slot_seats') || null}
        />
      </ConfigBlock>
    </Wrapper>
  );
};

export default ServiceForm;
